/**
 * Where this visit came from, captured once per browser session.
 *
 * Ads land on a deep page with `utm_*` in the URL, and the first client-side
 * navigation drops them. Anything that needs to know "this visitor came from
 * an ad" after that (the tour runner, the locked-state panel, tracking events)
 * reads the copy kept here instead of the current URL.
 *
 * First touch wins within a session: a later internal link with its own utm
 * tags (share cards, newsletters) must not overwrite the ad that brought the
 * visitor in.
 */

const STORAGE_KEY = "ortex_landing_utm";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export type LandingUtm = Partial<Record<(typeof UTM_KEYS)[number], string>>;

let cached: LandingUtm | null = null;

const readUrl = (): LandingUtm => {
    const out: LandingUtm = {};
    try {
        const params = new URLSearchParams(window.location.search);
        for (const key of UTM_KEYS) {
            const value = params.get(key);
            if (value) out[key] = value.slice(0, 255);
        }
    } catch {
        /* no window / malformed URL */
    }
    return out;
};

/** Store the landing utm tags if this session has none yet. Safe to call on every route change. */
export const captureLandingUtm = (): void => {
    if (typeof window === "undefined") return;
    const fromUrl = readUrl();
    try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
            cached = JSON.parse(stored);
            return;
        }
        if (Object.keys(fromUrl).length) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    } catch {
        /* private mode: fall back to the in-memory copy */
    }
    if (!cached && Object.keys(fromUrl).length) cached = fromUrl;
};

/** The utm tags this session landed with (empty object when none). */
export const getLandingUtm = (): LandingUtm => {
    if (typeof window === "undefined") return {};
    if (cached) return cached;
    try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (stored) cached = JSON.parse(stored);
    } catch {
        /* ignore */
    }
    return cached ?? readUrl();
};

/**
 * True when the visit came from paid traffic: a ChatGPT ad (utm_source=chatgpt,
 * or OpenAI's own `oppref` click id) or any cost-per-click campaign.
 */
export const isPaidAdVisit = (): boolean => {
    const utm = getLandingUtm();
    if ((utm.utm_source || "").toLowerCase() === "chatgpt") return true;
    if ((utm.utm_medium || "").toLowerCase() === "cpc") return true;
    try {
        return new URLSearchParams(window.location.search).has("oppref");
    } catch {
        return false;
    }
};
